import {DOM, JSX} from "lib/commons/xml/dom";
import {RespError} from "lib/commons/io/io";
import {IError, IErrorCtx, IErrorLog} from "lib/commons/errorLog";
import {REG} from "lib/commons/registry";
import "lib/commons/errorLog";
import {POPUP} from "back/commons/widgets/popups";
import {ErrorViewer} from "back/core/dialogs/errorViewer";

export namespace ERROR {

	/** Diffusion d'une erreur dans le système de log ET affichée à l'utilisateur. */
	export async function report(msg: string, cause?: Error, ctx?: IErrorCtx): Promise<void> {
		return reportError({msg, cause, ctx, adminDetails: await extractAdminDetails(cause)});
	}

	/** Affichage d'une erreur à l'utilisateur, sans diffusion dans le système de log */
	export async function show(msg: string, cause?: Error, ctx?: IErrorCtx): Promise<void> {
		return showError({msg, cause, ctx, adminDetails: await extractAdminDetails(cause)});
	}

	/** Diffusion d'une erreur dans le système de log sans notification à l'utilisateur. */
	export async function log(msg: string, cause?: Error, ctx?: IErrorCtx): Promise<void> {
		return logError({msg, cause, ctx, adminDetails: await extractAdminDetails(cause)});
	}

	export async function reportError(error: IError): Promise<void> {
		await logError(error);
		await showError(error);
	}

	export async function showError(error: IError): Promise<void> {
		try {
			await POPUP.showDialog(new ErrorViewer().initialize({error}), document.body, {
				titleBar: {barLabel: {label: "Erreur"}},
				resizer: {},
				initWidth: '40em',
				fixSize: false
			}).onNextClose();
		} catch (e) {
			console.error(e);
		}
	}

	export async function logError(error: IError): Promise<void> {
		try {
			const errorLog = REG.reg.getSvc<IErrorLog>("errorLog");
			if (errorLog) errorLog.logError(error);
			else console.error(error.msg, error.adminDetails, error.cause);
		} catch (e) {
			console.error(e);
		}
	}

	/** Tente d'extraire la description d'un message Scenari à partir d'une erreur issue d'un fetch */
	export async function extractDescError(cause: Error): Promise<string | null> {
		if (cause instanceof RespError) {
			const traceDoc = await extractXmlDoc(cause);
			return traceDoc ? traceDoc.documentElement.getAttribute("desc") : null;
		}
	}

	/** Tente d'extraire un adminDetails à partir d'une erreur issue d'un fetch */
	export async function extractAdminDetails(cause: Error): Promise<string> {
		if (cause instanceof RespError) {
			const traceDoc = await extractXmlDoc(cause);
			if (traceDoc) return `url: ${cause.response.url}\ntrace:\n${DOM.ser(DOM.indentDom(traceDoc))}`;
		}
		return null;
	}

	/** Tente d'extraire un document XML à partir d'une erreur issue d'un fetch */
	export async function extractXmlDoc(cause: Error): Promise<Document | null> {
		if (cause instanceof RespError) {
			try {
				const respContent = await cause.response.text();
				return DOM.parseDomValid(respContent);
			} catch (e) {
				console.log(e);
			}
		}
		return null;
	}

	/** Construit le rapport complet à partir des différentes informations disponible dans l'erreur */
	export function getReport(error: IError): string {
		let report = error.msg;
		if (error.details) report += `\n\n${error.details}`;
		if (error.adminDetails) report += `\n\n--- Admin details ---\n${error.adminDetails}`;
		if (error.ctx) {
			report += `\n\n--- Context ---`;
			for (const propName in error.ctx) {
				if (propName === 'shortDesc' || propName === 'shortDescs') {
					const json = JSON.stringify(error.ctx[propName], simpleObjectReplacer);
					report += `\n${propName}: ${json}`;
				} else if (propName === 'reg' && 'getRegDef' in error.ctx[propName]) {
					report += `\nreg: ${JSON.stringify(error.ctx[propName].getRegDef())}`;
				} else {
					try {
						report += `\n${propName}: ${JSON.stringify(error.ctx[propName], simpleObjectReplacer)}`;
					} catch (e) {}
				}
			}
		}
		if (error.cause) report += `\n\n--- Js stack ---\n${error.cause.stack}`;
		report += `\n\n--- App context ---
url: ${window.location.href},
timestamp: ${new Date().toLocaleString()}
userAgent: ${navigator.userAgent}`;
		return report;
	}
}

/***
 * Une fonction de remplacement pour JSON.stringify qui enlève tout les objects complexes (cad avec une classe non native)
 */
function simpleObjectReplacer(key: string, value: any): any {
	if (value == null || typeof value !== 'object') return value;
	const constr = value.constructor;
	return constr === Object || constr === Array || constr === Date ? value : undefined;
}
